import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Mail, MessageSquare, Bell, AlertTriangle, RotateCw, Activity } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * <RecentInviteActivityCard /> — Iter-186.
 *
 * Drop-in card for AdminShiftDetail that lists the last 5 invite logs
 * tied to the shift. Each row shows the channel breakdown, a sample
 * failure reason (if any), and a "Retry failed" CTA when applicable.
 *
 * Props:
 *   shiftId: string
 */
const RecentInviteActivityCard = ({ shiftId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/notifications/logs?shift_id=${shiftId}&limit=5`);
      setLogs(res.data || []);
    } catch (e) {
      // 404 on the endpoint is fine — feature might not be deployed yet.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shiftId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftId]);

  const retry = async (log) => {
    const retryable = (log.failure_samples || []).filter(s => !s.skipped);
    if (!retryable.length) {
      toast.info('Nothing to retry — all blocked rows are environment skips, not failures.');
      return;
    }
    setRetrying(log.id);
    try {
      const res = await api.post(`/admin/notifications/logs/${log.id}/retry-failed`);
      const r = res.data?.result || {};
      const sent = (r.sent?.email || 0) + (r.sent?.in_app || 0) + (r.sent?.sms || 0);
      toast.success(`Retried ${res.data?.retried || 0} recipient${res.data?.retried === 1 ? '' : 's'} — ${sent} sent`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white" data-testid="invite-activity-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-600" />
            Recent Invite Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return null; // hide quietly; admin will only see this when there's activity
  }

  return (
    <Card className="bg-white" data-testid="invite-activity-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-600" />
          Recent Invite Activity
          <Badge variant="outline" className="text-[10px] ml-auto">{logs.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {logs.map((log) => {
          const sent = log.sent_breakdown || {};
          const failed = log.failed_breakdown || {};
          const skipped = log.skipped_breakdown || {};
          // Iter-296 P0 — separate true failures from env-guard skips so
          // the UI doesn't flag perfectly-fine sends as "failed" when
          // they were just held back by the preview comm_guard.
          const realFailureSamples = (log.failure_samples || []).filter(s => !s.skipped);
          const skippedSamples = (log.failure_samples || []).filter(s => s.skipped);
          const realFailed = (failed.email || 0) + (failed.in_app || 0) + (failed.sms || 0);
          const totalSkipped = (skipped.email || 0) + (skipped.in_app || 0) + (skipped.sms || 0);
          const totalSent = (sent.email || 0) + (sent.in_app || 0) + (sent.sms || 0);
          // Iter-297e — Legacy fallback. If the row pre-dates the breakdown
          // schema (no per-channel counts saved) we surface the raw totals
          // without the misleading red "FAILED" chip, since we can't tell
          // whether they were comm_guard skips or real failures.
          const hasBreakdown = totalSent + realFailed + totalSkipped > 0;
          const legacyTotalFailed = !hasBreakdown ? (log.total_failed || 0) : 0;
          const legacyTotalSent = !hasBreakdown ? (log.total_sent || 0) : 0;
          const sampleReason = (realFailureSamples[0]?.reason || '').slice(0, 120);
          const skipReason = (skippedSamples[0]?.reason || '').slice(0, 120);
          const ts = log.created_at ? new Date(log.created_at) : null;
          // Iter-362 (P0 SMS INCIDENT) — Timestamp rendering pinned to
          // America/New_York with an explicit TZ label so admins never
          // misread the local browser time as the event-location time.
          const tsFormatted = ts
            ? ts.toLocaleString('en-US', {
                timeZone: 'America/New_York',
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                timeZoneName: 'short',
              })
            : '';
          return (
            <div
              key={log.id}
              className="border border-gray-200 rounded-lg p-3 space-y-2"
              data-testid={`invite-log-${log.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span data-testid={`invite-log-${log.id}-timestamp`}>{tsFormatted}</span>
                  {log.queued_async && (
                    <Badge variant="outline" className="text-[10px] bg-violet-50 border-violet-200 text-violet-700">
                      Async batch
                    </Badge>
                  )}
                </div>
                {realFailed > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => retry(log)}
                    disabled={retrying === log.id || !realFailureSamples.length}
                    data-testid={`retry-failed-${log.id}`}
                  >
                    {retrying === log.id ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <RotateCw className="w-3 h-3 mr-1" />
                    )}
                    Retry {realFailureSamples.length || realFailed} failed
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="flex items-center gap-1 flex-wrap">
                  <Mail className="w-3 h-3 text-cyan-500" />
                  <span className="text-gray-700">{sent.email || 0} sent</span>
                  {(failed.email || 0) > 0 && <span className="text-rose-500">· {failed.email} failed</span>}
                  {(skipped.email || 0) > 0 && <span className="text-amber-600">· {skipped.email} skipped</span>}
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <Bell className="w-3 h-3 text-cyan-500" />
                  <span className="text-gray-700">{sent.in_app || 0} sent</span>
                  {(failed.in_app || 0) > 0 && <span className="text-rose-500">· {failed.in_app} failed</span>}
                  {(skipped.in_app || 0) > 0 && <span className="text-amber-600">· {skipped.in_app} skipped</span>}
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <MessageSquare className="w-3 h-3 text-cyan-500" />
                  <span className="text-gray-700">{sent.sms || 0} sent</span>
                  {(failed.sms || 0) > 0 && <span className="text-rose-500">· {failed.sms} failed</span>}
                  {(skipped.sms || 0) > 0 && <span className="text-amber-600">· {skipped.sms} skipped</span>}
                </div>
              </div>

              {!hasBreakdown && (legacyTotalSent > 0 || legacyTotalFailed > 0) && (
                <div
                  className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1"
                  data-testid={`invite-log-${log.id}-legacy-banner`}
                >
                  Legacy invite log — channel breakdown not recorded.
                  {' '}{legacyTotalSent} delivered · {legacyTotalFailed} not delivered.
                </div>
              )}

              {totalSkipped > 0 && (
                <div
                  className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1"
                  data-testid={`invite-log-${log.id}-skipped-banner`}
                >
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span className="break-words" title={skipReason}>
                    {/* Iter-362 (P0 SMS INCIDENT) — Explicit per-channel
                        breakdown so operators can tell exactly which
                        channel was blocked. Preview banners previously
                        said "392 held back" without saying which. */}
                    {totalSkipped} held back by environment guard
                    {' ('}
                    {(skipped.email || 0) > 0 && `${skipped.email} email`}
                    {(skipped.email || 0) > 0 && ((skipped.sms || 0) > 0 || (skipped.in_app || 0) > 0) && ', '}
                    {(skipped.sms || 0) > 0 && `${skipped.sms} SMS`}
                    {(skipped.sms || 0) > 0 && (skipped.in_app || 0) > 0 && ', '}
                    {(skipped.in_app || 0) > 0 && `${skipped.in_app} in-app`}
                    {')'}
                    {skipReason ? ` — ${skipReason}` : ''}.
                    {' '}These would send normally in production.
                    <br />
                    <span className="text-[10px] text-amber-700/80 font-semibold">
                      If you are seeing this on the LIVE production site, run{' '}
                      <code className="bg-white/60 px-1 py-0.5 rounded">
                        GET /api/admin/system/sms-pipeline-diagnostic
                      </code>
                      {' '}— your pod&apos;s <code>APP_ENV</code> is not set to <code>production</code>.
                    </span>
                  </span>
                </div>
              )}

              {sampleReason && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span className="truncate" title={sampleReason}>{sampleReason}</span>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default RecentInviteActivityCard;
